import {
  useState,
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useSyncExternalStore,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AppSidebar } from '@/components/app-sidebar'
import { prefetchWorkflowData } from '@/api/use-workflow-runs-query'
import { AskCraftFloatingButton } from '@/components/chat/ask-craft-floating-button'
import { useChatContext, useChatContextActions } from '@/components/chat/chat-context'
import {
  getStoredChatSidebarOpen,
  setStoredChatSidebarOpen,
} from '@/lib/chat-sidebar-open-storage'

const CHAT_WIDTH_KEY = 'chatSidebarWidth'
const CHAT_WIDTH_DEFAULT = 320

/** Keep main pane at least this wide when possible (matches drag resize). */
const MIN_MAIN_WIDTH = 480
const MIN_CHAT_WIDTH = 260
/** When the viewport is too narrow for both mins, allow main down to this before shrinking chat below MIN_CHAT_WIDTH. */
const SOFT_MAIN_MIN = 280

/**
 * Clamp stored / dragged chat width so main + chat never exceeds the layout row (avoids horizontal overflow).
 */
function clampChatWidth(layoutWidth: number, desired: number): number {
  if (layoutWidth <= 0 || !Number.isFinite(desired)) return CHAT_WIDTH_DEFAULT
  const maxPreferred = layoutWidth - MIN_MAIN_WIDTH
  if (maxPreferred >= MIN_CHAT_WIDTH) {
    return Math.max(MIN_CHAT_WIDTH, Math.min(desired, maxPreferred))
  }
  const cap = Math.max(0, layoutWidth - SOFT_MAIN_MIN)
  return Math.min(desired, cap)
}

function getStoredChatWidth(): number {
  if (typeof window === 'undefined') return CHAT_WIDTH_DEFAULT
  try {
    const stored = localStorage.getItem(CHAT_WIDTH_KEY)
    return stored ? Number(stored) : CHAT_WIDTH_DEFAULT
  } catch {
    return CHAT_WIDTH_DEFAULT
  }
}

/** Viewports at or below this width use a full-pane chat overlay. */
const CHAT_OVERLAY_MAX_WIDTH_PX = 800

const narrowChatOverlayQuery = `(max-width: ${CHAT_OVERLAY_MAX_WIDTH_PX}px)`

function subscribeNarrowChatOverlay(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {}
  const mq = window.matchMedia(narrowChatOverlayQuery)
  mq.addEventListener('change', onStoreChange)
  return () => mq.removeEventListener('change', onStoreChange)
}

function getNarrowChatOverlaySnapshot(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(narrowChatOverlayQuery).matches
}

function getServerNarrowChatOverlaySnapshot(): boolean {
  return false
}

// Lazy load A2A chat; start loading immediately on page load so it's ready when user opens
const chatSidebarModulePromise = import('@/components/chat')
const ChatSidebar = lazy(() => chatSidebarModulePromise.then((m) => ({ default: m.ChatSidebar })))

export function MainLayout({ children }: React.PropsWithChildren) {
  const useChatOverlayLayout = useSyncExternalStore(
    subscribeNarrowChatOverlay,
    getNarrowChatOverlaySnapshot,
    getServerNarrowChatOverlaySnapshot,
  )
  const { isChatOpen: contextWantsChatOpen, reportId: contextReportId } = useChatContext()
  const { setContext: setChatContext, prepareHeavyChartsForChatOpen } = useChatContextActions()

  const [chatOpen, setChatOpen] = useState(getStoredChatSidebarOpen)
  const [hasInitiatedChat, setHasInitiatedChat] = useState(chatOpen)
  const [chatWidth, setChatWidth] = useState(getStoredChatWidth)
  const [isResizingChat, setIsResizingChat] = useState(false)
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const resizeSessionRef = useRef<{
    totalWidth: number
    right: number
  } | null>(null)
  const pendingPointerXRef = useRef<number | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const queryClient = useQueryClient()

  /** Let heavy charts commit to static preview before the sidebar width animation runs. */
  const scheduleChatPanelOpen = useCallback(() => {
    prepareHeavyChartsForChatOpen(contextReportId ?? null)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setHasInitiatedChat(true)
        setChatOpen(true)
      })
    })
  }, [prepareHeavyChartsForChatOpen, contextReportId])

  const openChat = () => {
    scheduleChatPanelOpen()
  }

  const handleChatOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        scheduleChatPanelOpen()
        return
      }
      setChatOpen(false)
    },
    [scheduleChatPanelOpen],
  )

  const handleChatResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    const el = layoutRef.current
    if (!el) return
    const bounds = el.getBoundingClientRect()
    if (bounds.width <= 0) return
    resizeSessionRef.current = { totalWidth: bounds.width, right: bounds.right }
    setIsResizingChat(true)
  }, [])

  useEffect(() => {
    if (!isResizingChat) return

    const applyFromPointerX = (pointerX: number) => {
      const session = resizeSessionRef.current
      if (!session) return

      const { totalWidth, right } = session
      let nextWidth = right - pointerX
      if (!Number.isFinite(nextWidth)) return

      nextWidth = Math.max(MIN_CHAT_WIDTH, nextWidth)
      const mainWidth = totalWidth - nextWidth
      if (mainWidth < MIN_MAIN_WIDTH) {
        nextWidth = Math.max(MIN_CHAT_WIDTH, totalWidth - MIN_MAIN_WIDTH)
      }

      if (nextWidth < 0) nextWidth = 0
      setChatWidth(clampChatWidth(totalWidth, nextWidth))
    }

    const schedule = () => {
      if (rafIdRef.current != null) return
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null
        const pointerX = pendingPointerXRef.current
        if (pointerX == null) return
        applyFromPointerX(pointerX)
      })
    }

    const handleMouseMove = (event: MouseEvent) => {
      pendingPointerXRef.current = event.clientX
      schedule()
    }

    const handleMouseUp = () => {
      setIsResizingChat(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      pendingPointerXRef.current = null
      resizeSessionRef.current = null
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [isResizingChat])

  useEffect(() => {
    // Prefetch can be useful after login, but it shouldn't compete with initial-page critical queries.
    // Defer until the browser is idle to avoid making the first navigation feel "stuck" if backend is slow.
    const handle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(() => prefetchWorkflowData(queryClient), { timeout: 5000 })
        : window.setTimeout(() => prefetchWorkflowData(queryClient), 1500)
    return () => {
      if (typeof window.cancelIdleCallback === 'function' && typeof window.requestIdleCallback === 'function') {
        window.cancelIdleCallback(handle)
        return
      }
      window.clearTimeout(handle)
    }
  }, [queryClient])

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidth))
    } catch {
      /* ignore */
    }
  }, [chatWidth])

  useEffect(() => {
    setStoredChatSidebarOpen(chatOpen)
  }, [chatOpen])

  useEffect(() => {
    if (chatOpen) {
      setChatContext({ isChatOpen: true })
    } else {
      setChatContext({
        isChatOpen: false,
        reportId: undefined,
        reportName: undefined,
        reportPinned: undefined,
        chartContext: [],
      })
    }
  }, [chatOpen, setChatContext])

  // When add-to-context (or setContext) sets isChatOpen, open the panel after charts downgrade.
  useEffect(() => {
    if (!contextWantsChatOpen) return
    if (chatOpen && hasInitiatedChat) return

    if (!chatOpen) {
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setHasInitiatedChat(true)
          setChatOpen(true)
        })
      })
      return () => cancelAnimationFrame(frameId)
    }

    setHasInitiatedChat(true)
  }, [contextWantsChatOpen, chatOpen, hasInitiatedChat])

  // Keep chat width within the layout row when viewport changes, sidebar opens, or after navigation.
  // On narrow viewports the chat is a full-width overlay; use the full layout width instead of
  // clamping to ~0px (desktop `MIN_MAIN_WIDTH` logic would otherwise hide the panel on phones).
  useLayoutEffect(() => {
    const el = layoutRef.current
    if (!el) return

    const apply = () => {
      const w = el.getBoundingClientRect().width
      if (w <= 0) return
      if (useChatOverlayLayout) {
        // Full-viewport overlay (covers app rail); keep width in sync for chat internals.
        if (chatOpen) setChatWidth(window.innerWidth)
        return
      }
      setChatWidth((prev) => clampChatWidth(w, prev))
    }

    apply()
    const ro = new ResizeObserver(() => apply())
    ro.observe(el)
    window.addEventListener('resize', apply)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', apply)
    }
  }, [chatOpen, useChatOverlayLayout])

  return (
    <div className='flex h-svh min-h-0 w-full max-w-full overflow-hidden bg-background'>
      <AppSidebar />
      <main className='flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden'>
        <div
          ref={layoutRef}
          className='relative flex h-full min-h-0 min-w-0 max-w-full overflow-hidden'
        >
          <div className='min-h-0 min-w-0 flex-1 overflow-auto'>
            {children}
          </div>
          {hasInitiatedChat && (
            <Suspense fallback={null}>
              <ChatSidebar
                open={chatOpen}
                onOpenChange={handleChatOpenChange}
                width={chatWidth}
                onResizeStart={handleChatResizeStart}
                layoutVariant={useChatOverlayLayout ? 'overlay' : 'dock'}
              />
            </Suspense>
          )}
          {!chatOpen && <AskCraftFloatingButton onClick={openChat} />}
        </div>
      </main>
    </div>
  )
}
